import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Section, Reveal, Mono } from "@/components/network/NetworkAtoms";

/**
 * One application, sorted into three piles. Drawn as an allocation bar and three equal rows —
 * no hand-drawn routes, nothing that can drift out of line.
 */
const PANEL_SIZE = 28;

const GROUPS = [
  {
    k: "fits", label: "Fits your business", count: 8,
    note: "These are the ones who see it",
    edge: "border-l-[#22c55e]", chip: "bg-[#22c55e]/[0.055]",
    seg: "bg-[#22c55e]", num: "text-[#22c55e]",
  },
  {
    k: "near", label: "Criteria checked, not this time", count: 5,
    note: "Close, but something didn't line up",
    edge: "border-l-[#7da9ff]/60", chip: "bg-white/[0.025]",
    seg: "bg-[#7da9ff]/55", num: "text-[#7da9ff]",
  },
  {
    k: "out", label: "Outside their appetite", count: 15,
    note: "Never contacted, never sent your details",
    edge: "border-l-white/15", chip: "bg-white/[0.012]",
    seg: "bg-white/[0.13]", num: "text-slate-400",
  },
];

const SEGMENTS = GROUPS.flatMap((g) => Array.from({ length: g.count }, () => g.seg));

const STEPS = [
  { k: "read", label: "It is read, properly", body: "A person reviews your case the day it arrives: what you need, what supports it, and what a funder will ask for before they commit." },
  { k: "match", label: "We match, we don't broadcast", body: "Your file is scored against each funder's live criteria — sector, trading history, turnover, security, ticket size. Only the funders who can write it see it." },
  { k: "compete", label: "Terms come back to one desk", body: "Funders respond with terms or questions. You get a single summary from a single contact, not eight conversations with eight credit teams." },
];

export default function PanelRouting() {
  const reduce = useReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setTick((t) => (t + 1) % 3), 2600);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <Section
      testId="funding-routing"
      eyebrow="How it works"
      title="Your application isn't queued. It's placed with the funders who can write it."
      lede="A clearing bank applies one credit policy. A panel is dozens of different appetites, and the work is
            knowing which of them fits the business in front of it — sector, size, security and structure."
    >
      <div className="mt-12 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-14 items-center">
        <div className="order-2 lg:order-1 space-y-3.5">
          {STEPS.map((s, i) => {
            const on = !reduce && tick === i;
            return (
              <Reveal key={s.k} delay={i * 0.08}>
                <div className={`rounded-2xl border p-5 sm:p-6 transition-colors duration-700 ${
                  on ? "border-[#0055FF]/40 bg-[#0055FF]/[0.07]" : "border-white/10 bg-white/[0.02]"}`}
                     data-testid={`routing-step-${s.k}`}>
                  <div className="flex items-center gap-3">
                    <span className={`grid place-items-center size-7 rounded-full text-[11px] font-mono transition-colors duration-700 ${
                      on ? "bg-[#0055FF] text-white" : "bg-white/[0.06] text-slate-400"}`}>{i + 1}</span>
                    <h3 className="text-[16px] font-semibold text-white tracking-tight">{s.label}</h3>
                  </div>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-slate-300 sm:pl-10">{s.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="order-1 lg:order-2">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0B1220]/60 p-5 backdrop-blur-xl sm:p-8"
               data-testid="funding-routing-visual">
            <div aria-hidden className="absolute -top-24 right-0 h-72 w-72 rounded-full bg-[#0055FF] opacity-[0.16] blur-[110px]" />

            {/* your file */}
            <div className="relative flex items-center gap-3.5 rounded-2xl border border-[#0055FF]/30 bg-[#0055FF]/[0.09] p-3.5 sm:p-4">
              <span className="grid size-10 shrink-0 place-items-center rounded-xl border border-[#0055FF]/40 bg-[#0055FF]/20 font-cabinet text-[13px] font-bold text-white">
                You
              </span>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold leading-tight text-white">One application, read by a person</div>
                <div className="mt-1 text-[11.5px] leading-snug text-slate-400">
                  Nothing leaves this desk until you've approved where it goes
                </div>
              </div>
            </div>

            {/* the panel, one desk per segment */}
            <div className="relative mt-6 flex items-center justify-between gap-3">
              <Mono>Scored against the panel</Mono>
              <Mono className="text-slate-500">{PANEL_SIZE} desks</Mono>
            </div>
            <div aria-hidden className="relative mt-3 flex items-stretch gap-[2px] sm:gap-[3px]">
              {SEGMENTS.map((seg, i) => (
                <motion.span
                  key={i}
                  className={`h-7 flex-1 rounded-[2px] ${seg}`}
                  initial={reduce ? false : { opacity: 0, scaleY: 0.35 }}
                  whileInView={{ opacity: 1, scaleY: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: 0.1 + i * 0.02 }}
                />
              ))}
            </div>

            {/* what the colours mean */}
            <div className="relative mt-5 space-y-2">
              {GROUPS.map((g, gi) => (
                <motion.div
                  key={g.k}
                  initial={reduce ? false : { opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: 0.75 + gi * 0.1 }}
                  className={`flex items-center gap-4 rounded-xl border border-white/[0.07] border-l-2 ${g.edge} ${g.chip} px-3.5 py-3 sm:px-4`}
                  data-testid={`panel-group-${g.k}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.5px] font-semibold leading-tight text-white" title={g.label}>{g.label}</div>
                    <div className="mt-1 truncate text-[11px] leading-snug text-slate-400" title={g.note}>{g.note}</div>
                  </div>
                  <div className="w-14 shrink-0 text-right leading-none">
                    <span className={`font-cabinet text-[19px] font-bold tabular-nums ${g.num}`}>{g.count}</span>
                    <span className="font-mono text-[11px] tabular-nums text-slate-500">/{PANEL_SIZE}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            <p className="relative mt-6 border-t border-white/10 pt-4 text-[11.5px] leading-relaxed text-slate-400">
              An illustration, not live data. Funders stay anonymous here for their own commercial privacy — but
              you'll always be told who is considering your application, and nothing is submitted anywhere
              without your say-so.
            </p>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
